/**
 * React Query options and hooks for the product catalog (T-365). Every key carries the workspace,
 * and no query runs before both it and the id are known.
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import {
  getDocumentProductExtractions,
  getProductRecord,
  listProductRecords,
  updateProductRecord,
  type ProductRecordDetail,
  type ProductRecordStatus,
  type ProductRecordsPage,
} from "../services/naraDoor";
import { getCurrentWorkspaceId } from "../lib/workspaceStore";

export interface CatalogFilters {
  status?: ProductRecordStatus;
  q?: string;
}

export const productRecordsOptions = (workspaceId: string | undefined, projectId: string | undefined, { status, q }: CatalogFilters) => ({
  queryKey: ["productRecords", workspaceId, projectId, status ?? "", q ?? ""],
  queryFn: ({ pageParam }: { pageParam: string | undefined }) => listProductRecords(workspaceId!, projectId!, { status, q, cursor: pageParam }),
  initialPageParam: undefined as string | undefined,
  getNextPageParam: (last: ProductRecordsPage) => last.next_cursor ?? undefined,
  enabled: !!workspaceId && !!projectId,
});

export const productRecordOptions = (workspaceId: string | undefined, recordId: string | undefined) => ({
  queryKey: ["productRecord", workspaceId, recordId],
  queryFn: () => getProductRecord(recordId!),
  enabled: !!workspaceId && !!recordId,
});

export const documentProductExtractionsOptions = (workspaceId: string | undefined, documentId: string | undefined) => ({
  queryKey: ["productExtractions", workspaceId, documentId],
  queryFn: () => getDocumentProductExtractions(documentId!),
  enabled: !!workspaceId && !!documentId,
});

/** Save and Approve are one mutation: the patch decides. The open record takes the answer; the list reloads. */
export const updateProductRecordOptions = (client: QueryClient, workspaceId: string | undefined, projectId: string | undefined, recordId: string) => ({
  mutationFn: (patch: Parameters<typeof updateProductRecord>[1]) => updateProductRecord(recordId, patch),
  onSuccess: (record: ProductRecordDetail) => {
    client.setQueryData(["productRecord", workspaceId, recordId], record);
    return client.invalidateQueries({ queryKey: ["productRecords", workspaceId, projectId] });
  },
});

export const useProductRecords = (projectId: string | undefined, filters: CatalogFilters) =>
  useInfiniteQuery(productRecordsOptions(getCurrentWorkspaceId() ?? undefined, projectId, filters));

export const useProductRecord = (recordId: string | undefined) => useQuery(productRecordOptions(getCurrentWorkspaceId() ?? undefined, recordId));

export const useDocumentProductExtractions = (documentId: string | undefined) =>
  useQuery(documentProductExtractionsOptions(getCurrentWorkspaceId() ?? undefined, documentId));

export function useUpdateProductRecord(projectId: string | undefined, recordId: string) {
  const client = useQueryClient();
  return useMutation(updateProductRecordOptions(client, getCurrentWorkspaceId() ?? undefined, projectId, recordId));
}
